import { useInfiniteQuery } from '@tanstack/react-query'
import axios, { AxiosError, AxiosResponse } from 'axios'
import * as React from 'react'
import { InView } from 'react-intersection-observer'
import { NfdRecord } from '@/api/api-client'
import { Loading, Spinner } from '@/components/LoadingSpinner'
import { API_BASE_URL } from '@/data/constants'
import { Asset } from '@/types/assets'
import { NftResult } from './NftResult'

export type PagedGalleryData = {
  results: Asset[]
  nextPage: number | null
}

interface NftsProps {
  nfd: NfdRecord
  isOpen: boolean
  setSelectedAsset: (asset: Asset) => void
}

export function Nfts({ nfd, isOpen, setSelectedAsset }: NftsProps) {
  const {
    data: nfts,
    isLoading,
    error,
    fetchNextPage,
    isFetchingNextPage
  } = useInfiniteQuery<PagedGalleryData, AxiosError>(
    ['nfts-avatar-banner', nfd.name],
    ({ pageParam = 1 }) => fetchNfts(nfd, pageParam),
    {
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      getNextPageParam: (lastPage) => lastPage.nextPage,
      enabled: isOpen
    }
  )

  const renderNftsPage = (results: Asset[], hasNextPage: number | null, pageIdx: number) => {
    if (!nfts) {
      return null
    }

    return results.map((asset: Asset, i) => {
      if (i === results.length - 1) {
        // last element in the page
        if (pageIdx === nfts.pages.length - 1 && hasNextPage) {
          // this is last page, and there is a next page
          return (
            <InView key={asset.id} onChange={(inView) => inView && fetchNextPage()}>
              {({ ref }) => (
                <div ref={ref}>
                  <NftResult asset={asset} onAssetClick={setSelectedAsset} />
                </div>
              )}
            </InView>
          )
        }
      }

      return <NftResult key={asset.id} asset={asset} onAssetClick={setSelectedAsset} />
    })
  }

  if (isLoading) {
    return <Loading className="min-h-96" />
  }

  if (error) {
    return <div>Error</div>
  }

  if (!nfts || !nfts.pages[0]?.results?.length) {
    return <div>No results</div>
  }

  return (
    <>
      <div className="grid grid-cols-[repeat(auto-fill,minmax(80px,1fr))] sm:grid-cols-[repeat(auto-fill,minmax(100px,1fr))] gap-4 p-1 sm:mr-8">
        {nfts.pages.map((page, pageIdx) => {
          return (
            <React.Fragment key={pageIdx}>
              {renderNftsPage(page.results, page.nextPage, pageIdx)}
            </React.Fragment>
          )
        })}
      </div>

      {isFetchingNextPage && (
        <div className="w-full flex flex-col items-center justify-center py-16">
          <Spinner />
        </div>
      )}
    </>
  )
}

async function fetchNfts(nfd: NfdRecord, page: number, limit = 20): Promise<PagedGalleryData> {
  function fetchPage(pageNum: number) {
    const offset = (pageNum - 1) * limit
    const url = `${API_BASE_URL}/nfd/assets/${nfd.name}?limit=${limit}&offset=${offset}`
    return axios.get<Asset[]>(url)
  }

  function filterResults(asset: Asset) {
    const isBelowSupplyThreshold = asset.totalCreated / Math.pow(10, asset.decimals) <= 10000
    const isNotNfd = asset.unitName?.toLowerCase() !== 'nfd'
    const isNotAlgoName = !asset.name?.toLowerCase().endsWith('.algo')
    return isBelowSupplyThreshold && isNotNfd && isNotAlgoName
  }

  let fetchedPage = page
  let results: AxiosResponse<Asset[], unknown>
  let filteredResults: Asset[] = []

  do {
    results = await fetchPage(fetchedPage)
    filteredResults = results.data.filter(filterResults)
    fetchedPage++
  } while (results.data.length > 0 && filteredResults.length === 0)

  if (!results.data.length) {
    return { results: [], nextPage: null }
  }

  return {
    results: filteredResults,
    nextPage: results.data.length === limit ? fetchedPage : null
  }
}
